import React, { useState } from 'react';
import { FileText, Plus, ChevronRight } from 'lucide-react';
import { Badge, Button, EmptyState, TextInput, Textarea } from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';
import { useTranslation, MIRROR_ICON } from '@parsina/framework/clinical/src/i18n/index';
import ConfirmDialog from '@parsina/framework/clinical/src/components/common/ConfirmDialog';
import { useMyPageStrings } from '../myPageStrings';
import { useDraft } from '../useDraft';
import { LIMITS, normalizeSlugInput, slugFormatError, saveStatus, overLimit } from '../MyPage.shared';
import { ErrorLine, ImageSlot, SaveRow, useFieldStyle } from '../parts/common';

// «مقالات و مطالب علمی» — independent of the page snapshot: each article is
// published on its own (design §7.3), and an article on an unpublished page is
// invisible whatever its status. The list shows where each stands; the editor
// is the sanitised-markdown subset with a live counter.

const articleDraft = (a) => ({ title: a?.title || '', slug: a?.slug || '', bodyMd: a?.bodyMd || '' });

function ArticleForm({ article, articles, actions, cards, readOnly, onDone }) {
  const s = useMyPageStrings();
  const fieldStyle = useFieldStyle();
  const [id, setId] = useState(article?.id || null);
  // Looked up by the form's OWN id, so an article created here picks up what
  // every later write says about it (status, cover, unpublished changes) —
  // the `article` prop stays null for a form opened as «new».
  const current = (id && (articles || []).find((a) => a.id === id)) || article || null;
  const { draft, setDraft, dirty, adopt, reset } = useDraft(articleDraft(current));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const key = `article-${id || 'new'}`;
  const card = cards[key] || {};
  const statusCard = cards[`article-status-${id}`] || {};
  const slugLocked = !!current?.slugLocked;
  const slugError = !slugLocked && draft.slug ? slugFormatError(normalizeSlugInput(draft.slug)) : '';
  const invalid = !draft.title.trim() || overLimit(draft.title, LIMITS.article_title_max)
    || overLimit(draft.bodyMd, LIMITS.article_body_max) || !!slugError;
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const save = async () => {
    const input = { title: draft.title.trim(), bodyMd: draft.bodyMd };
    if (id) input.id = id;
    if (!slugLocked && draft.slug.trim()) input.slug = normalizeSlugInput(draft.slug);
    const res = await actions.upsertArticle(key, input);
    const saved = res.ok ? res.data?.upsertDoctorPageArticle : null;
    if (saved) {
      if (!id) setId(saved.id);
      adopt(articleDraft(saved));
    }
  };

  const published = current?.status === 'published';
  return (
    <div style={{ display: 'grid', gap: '1rem' }} data-testid="my-page-article-form">
      <Button variant="ghost" size="sm" onClick={onDone} className="justify-self-start" data-testid="my-page-article-back">
        <ChevronRight size={16} aria-hidden style={MIRROR_ICON} />
        {s('myPage.articles.back')}
      </Button>
      <TextInput
        label={s('myPage.articles.title')}
        value={draft.title}
        onChange={set('title')}
        maxLength={LIMITS.article_title_max}
        disabled={readOnly}
        inputStyle={fieldStyle}
        data-testid="my-page-article-title"
      />
      {slugLocked ? (
        <div style={{ fontSize: '0.75rem', color: C.textSecondary }}>
          {s('myPage.articles.slugLocked')} <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>{current.slug}</span>
        </div>
      ) : (
        <TextInput
          label={s('myPage.articles.slug')}
          helperText={!slugError ? s('myPage.articles.slugHint') : undefined}
          error={slugError ? s(slugError) : undefined}
          dir="ltr"
          value={draft.slug}
          onChange={set('slug')}
          maxLength={LIMITS.slug_max}
          disabled={readOnly}
          inputStyle={fieldStyle}
          autoComplete="off"
        />
      )}
      <Textarea
        label={s('myPage.articles.body')}
        value={draft.bodyMd}
        onChange={set('bodyMd')}
        rows={14}
        maxLength={LIMITS.article_body_max}
        showCount
        disabled={readOnly}
        helperText={s('myPage.markdownHint')}
        inputStyle={fieldStyle}
        dir="auto"
        data-testid="my-page-article-body"
      />
      <SaveRow
        status={saveStatus({ dirty: dirty || !id, saving: card.saving, error: card.error, savedAt: card.savedAt })}
        error={card.error}
        onSave={save}
        onReset={id ? reset : undefined}
        disabled={readOnly || invalid}
        testId="my-page-article"
      />

      {id ? (
        <>
          <ImageSlot
            slot="article"
            label={s('myPage.articles.cover')}
            media={current?.coverUrl ? { url: current.coverUrl } : null}
            busy={!!cards[`img-article-${id}`]?.saving}
            disabled={readOnly}
            onUpload={async (image) => (await actions.uploadImage(`img-article-${id}`, {
              slot: 'article', image, refId: id, alt: draft.title.trim() || current?.title || '',
            })).ok}
            // Clearing goes through the article itself (coverFileId ''); the
            // service releases the file once nothing published shows it.
            onRemove={current?.coverUrl
              ? async () => { await actions.upsertArticle(`img-article-${id}`, { id, coverFileId: '' }); }
              : undefined}
            testId="my-page-article-cover"
          />
          <ErrorLine error={cards[`img-article-${id}`]?.error} />
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', borderTop: `1px solid ${C.border}`, paddingTop: '1rem' }}>
            <Button
              variant={published ? 'outline' : 'default'}
              size="sm"
              disabled={readOnly || statusCard.saving || dirty}
              onClick={() => actions.setArticleStatus(`article-status-${id}`, id, published ? 'draft' : 'published')}
              data-testid="my-page-article-status"
            >
              {published
                ? s('myPage.articles.unpublish')
                : s('myPage.articles.publish')}
            </Button>
            {published && current?.hasUnpublishedChanges ? (
              <Button
                size="sm"
                disabled={readOnly || statusCard.saving || dirty}
                onClick={() => actions.setArticleStatus(`article-status-${id}`, id, 'published')}
              >
                {s('myPage.articles.republish')}
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" disabled={readOnly} onClick={() => setConfirmDelete(true)} data-testid="my-page-article-delete">
              {s('myPage.articles.delete')}
            </Button>
          </div>
          {dirty ? <div style={{ fontSize: '0.75rem', color: C.textSecondary }}>{s('myPage.articles.saveFirst')}</div> : null}
          <ErrorLine error={statusCard.error} />
          <ErrorLine error={cards[`article-delete-${id}`]?.error} />
          <ConfirmDialog
            open={confirmDelete}
            onOpenChange={setConfirmDelete}
            title={s('myPage.articles.deleteTitle')}
            description={s('myPage.articles.deleteBody', { title: current?.title || draft.title })}
            confirmLabel={s('myPage.articles.deleteConfirm')}
            destructive
            loading={!!cards[`article-delete-${id}`]?.saving}
            onConfirm={async () => {
              const res = await actions.deleteArticle(`article-delete-${id}`, id);
              setConfirmDelete(false);
              if (res.ok) onDone();
            }}
          />
        </>
      ) : (
        <div style={{ fontSize: '0.75rem', color: C.textSecondary }}>{s('myPage.articles.saveToContinue')}</div>
      )}
    </div>
  );
}

export default function ArticlesEditor({ page, actions, cards, readOnly }) {
  const s = useMyPageStrings();
  const { formatNumber, formatDate } = useTranslation();
  const [open, setOpen] = useState(null); // article id | 'new' | null
  const articles = page.articles || [];

  if (open) {
    const article = open === 'new' ? null : articles.find((a) => a.id === open) || null;
    return (
      <ArticleForm
        key={open}
        article={article}
        articles={articles}
        actions={actions}
        cards={cards}
        readOnly={readOnly}
        onDone={() => setOpen(null)}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: '0.75rem' }} data-testid="my-page-articles">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ flex: 1, fontSize: '0.75rem', color: C.textSecondary }}>
          {s('myPage.items.count', { count: formatNumber(articles.length), max: formatNumber(LIMITS.articles_max) })}
        </span>
        {!readOnly && articles.length < LIMITS.articles_max ? (
          <Button size="sm" variant="outline" onClick={() => setOpen('new')} data-testid="my-page-articles-new">
            <Plus size={16} aria-hidden /> {s('myPage.articles.new')}
          </Button>
        ) : null}
      </div>
      {articles.length ? (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.5rem' }}>
          {articles.map((a) => (
            <li key={a.id}>
              <Button
                variant="ghost"
                onClick={() => setOpen(a.id)}
                data-testid={`my-page-articles-item-${a.id}`}
                className="h-auto min-h-11 w-full justify-start rounded-xl px-3 py-2 font-normal"
                style={{ border: `1px solid ${C.border}`, borderRadius: RADIUS.card, textAlign: 'start', whiteSpace: 'normal' }}
              >
                <span style={{ flex: 1, minWidth: 0, display: 'grid', gap: '0.25rem' }}>
                  <span dir="auto" style={{ fontSize: '0.875rem', fontWeight: 600, color: C.textPrimary }}>{a.title || s('myPage.articles.untitled')}</span>
                  <span style={{ fontSize: '0.75rem', color: C.textSecondary }}>
                    {a.publishedAtMs ? s('myPage.articles.publishedOn', { date: formatDate(Number(a.publishedAtMs)) }) : s('myPage.articles.neverPublished')}
                  </span>
                </span>
                <span style={{ display: 'inline-flex', gap: '0.25rem', flexShrink: 0, flexWrap: 'wrap' }}>
                  <Badge variant={a.status === 'published' ? 'success' : 'outline'}>
                    {a.status === 'published' ? s('myPage.articles.statusPublished') : s('myPage.articles.statusDraft')}
                  </Badge>
                  {a.status === 'published' && a.hasUnpublishedChanges ? (
                    <Badge variant="warning">{s('myPage.articles.changed')}</Badge>
                  ) : null}
                </span>
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          icon={<FileText size={44} strokeWidth={1.5} aria-hidden />}
          title={s('myPage.articles.emptyTitle')}
          description={s('myPage.articles.emptyHint')}
          style={{ padding: '1.5rem 1rem' }}
        />
      )}
    </div>
  );
}
